import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Bot, Mic, Save, Loader2, Volume2, Square, Power, AlertTriangle } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { useRealtimeAudio } from "@/hooks/useRealtimeAudio";
import { authenticatedFetch } from "@/lib/authenticatedFetch";
import { VapiMenuInterviewAssistant } from "./VapiMenuInterviewAssistant";

// Feature flag for VAPI
const USE_VAPI = (import.meta as any).env.VITE_USE_VAPI === 'true';

interface MenuInterviewAssistantProps {
  menuItemId?: string;
  menuItemName?: string;
  menuItemDescription?: string;
  restaurantId: string;
  restaurantName?: string;
  interviewType?: 'menu_item' | 'restaurant';
  existingKnowledge?: any;
  onComplete?: () => void;
}

export function MenuInterviewAssistant({
  menuItemId,
  menuItemName,
  menuItemDescription,
  restaurantId,
  restaurantName,
  interviewType = 'menu_item',
  existingKnowledge,
  onComplete
}: MenuInterviewAssistantProps) {
  const { toast } = useToast();
  const [conversationHistory, setConversationHistory] = useState<Array<{ role: 'user' | 'assistant', content: string }>>([]);
  const [isSaving, setIsSaving] = useState(false);

  // If VAPI is enabled, use the VAPI-based interview assistant
  if (USE_VAPI) {
    return (
      <VapiMenuInterviewAssistant
        menuItemId={menuItemId}
        menuItemName={menuItemName}
        menuItemDescription={menuItemDescription}
        restaurantId={restaurantId}
        restaurantName={restaurantName}
        interviewType={interviewType}
        existingKnowledge={existingKnowledge}
        onComplete={onComplete}
      />
    );
  }

  // Use the Live Realtime Audio Hook
  const {
    status,
    connect,
    disconnect,
    startListening,
    stopListening,
    interrupt,
  } = useRealtimeAudio({
    restaurantId,
    restaurantName: 'Restaurant', // This will be improved by the system prompt
    sessionType: 'interviewer',
    interviewConfig: {
      type: interviewType,
      itemName: menuItemName,
      // We could pass existing knowledge here if we fetched it first
    },
    onTranscript: (text, role) => {
      setConversationHistory(prev => [...prev, { role, content: text }]);
    },
    onError: (error) => {
      toast({
        title: "Connection Error",
        description: error,
        variant: "destructive",
      });
    }
  });

  // Handle starting a new audio session
  const handleStartSession = () => {
    connect();
  };

  const handleVoiceButtonClick = () => {
    if (status === 'listening') {
      stopListening();
    } else if (status === 'connected' || status === 'speaking') {
      if (status === 'speaking') interrupt();
      startListening();
    }
  };

  const saveInterview = async () => {
    try {
      setIsSaving(true);

      const response = await authenticatedFetch('/api/ai/interview/save', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          menuItemId,
          restaurantId,
          interviewType,
          data: {}, // Backend will extract data from messages
          messages: conversationHistory,
        }),
      });

      if (!response.ok) {
        throw new Error('Save failed');
      }

      toast({
        title: "Interview Saved",
        description: "Your details have been saved successfully!",
      });

      setIsSaving(false);
      disconnect();

      if (onComplete) {
        onComplete();
      }
    } catch (error) {
      console.error('Save error:', error);
      toast({
        title: "Save Error",
        description: "Could not save interview. Please try again.",
        variant: "destructive",
      });
      setIsSaving(false);
    }
  };

  const getStatusText = () => {
    switch (status) {
      case 'connecting': return 'Connecting...';
      case 'connected': return 'Ready - Click Mic to Talk';
      case 'listening': return 'Listening...';
      case 'processing': return 'AI is thinking...';
      case 'speaking': return 'AI is speaking...';
      case 'disconnected': return 'Disconnected';
      default: return 'Stable';
    }
  };

  const canSave = conversationHistory.length > 2 && !isSaving;

  return (
    <Card className="w-full max-w-2xl mx-auto shadow-xl border-primary/20 bg-gradient-to-b from-card to-secondary/10" data-testid="card-interview-assistant">
      <CardHeader className="text-center">
        <div className="mx-auto bg-primary/10 w-12 h-12 rounded-full flex items-center justify-center mb-2">
          <Bot className="w-6 h-6 text-primary" />
        </div>
        <CardTitle className="text-2xl font-bold tracking-tight">
          AI Knowledge Interface
        </CardTitle>
        <CardDescription className="text-base">
          {interviewType === 'menu_item'
            ? `Teaching AI about: ${menuItemName || 'Menu Item'}`
            : 'Training the AI on your restaurant story'}
        </CardDescription>
      </CardHeader>

      {/* Warning Banner for Trade Secrets */}
      <div className="mx-6 mb-4 p-4 bg-amber-50 border border-amber-300 rounded-lg" data-testid="warning-trade-secrets">
        <div className="flex gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="space-y-1">
            <p className="text-sm font-semibold text-amber-800">
              ⚠️ Customer Visibility Notice
            </p>
            <p className="text-sm text-amber-700">
              The information you provide in this interview will be <strong>accessible to customers</strong> through our AI waiter.
              Please <strong>avoid sharing trade secrets</strong>, proprietary recipes, or any confidential business information
              you don't want publicly known.
            </p>
          </div>
        </div>
      </div>

      <CardContent className="space-y-6">
        <div className="flex flex-col items-center gap-6 py-4">
          <div className="relative">
            {/* Status Pulse */}
            {status !== 'disconnected' && (
              <div className={`absolute -inset-4 rounded-full opacity-20 animate-ping ${status === 'listening' ? 'bg-red-500' :
                status === 'speaking' ? 'bg-blue-500' : 'bg-primary'
                }`} />
            )}

            <Button
              size="lg"
              variant={status === 'listening' ? 'destructive' : 'default'}
              className="w-32 h-32 rounded-full shadow-2xl relative z-10 transition-all hover:scale-105 active:scale-95"
              onClick={handleVoiceButtonClick}
              disabled={status === 'disconnected' || status === 'connecting' || isSaving}
              data-testid="button-voice-interview"
            >
              {status === 'connecting' ? (
                <Loader2 className="w-12 h-12 animate-spin text-white" />
              ) : status === 'listening' ? (
                <Square className="w-12 h-12 text-white" />
              ) : status === 'speaking' ? (
                <Volume2 className="w-12 h-12 animate-pulse text-white" />
              ) : (
                <Mic className="w-12 h-12 text-white" />
              )}
            </Button>
          </div>

          <div className="text-center space-y-1">
            <p className={`text-lg font-semibold tracking-wide uppercase transition-colors ${status === 'listening' ? 'text-red-500' :
              status === 'speaking' ? 'text-blue-500' : 'text-primary'
              }`} data-testid="text-interview-status">
              {getStatusText()}
            </p>
            {status === 'disconnected' && (
              <Button variant="outline" size="sm" onClick={handleStartSession} className="mt-2">
                <Power className="w-4 h-4 mr-2" /> Start Audio Session
              </Button>
            )}
          </div>
        </div>

        {conversationHistory.length > 0 && (
          <div className="space-y-4 max-h-72 overflow-y-auto rounded-xl border bg-background/50 p-6 shadow-inner backdrop-blur-sm">
            {conversationHistory.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.role === 'assistant' ? 'justify-start' : 'justify-end'}`}
                data-testid={`text-message-${idx}`}
              >
                <div className={`max-w-[85%] rounded-2xl px-4 py-2 text-sm shadow-sm ${msg.role === 'assistant'
                  ? 'bg-secondary text-secondary-foreground rounded-tl-none'
                  : 'bg-primary text-primary-foreground rounded-tr-none'
                  }`}>
                  {msg.content}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="flex gap-4">
          {status !== 'disconnected' && (
            <Button variant="ghost" onClick={disconnect} className="text-muted-foreground hover:text-destructive">
              End Session
            </Button>
          )}

          {canSave && (
            <Button
              className="flex-1 font-bold py-6 text-lg shadow-lg hover:shadow-primary/20"
              onClick={saveInterview}
              disabled={isSaving}
              data-testid="button-save-interview"
            >
              {isSaving ? (
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              ) : (
                <Save className="w-5 h-5 mr-3" />
              )}
              {isSaving ? "Saving Knowledge..." : "Save Knowledge to Database"}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
